import {
  useRef, useState, useCallback, useEffect,
} from 'react';
import toast from 'react-hot-toast';
import api from 'api';
import util from 'utils/utils';
import websocketApi from 'api/websocket';
import { useStore } from 'react-redux';

export const reducePath = (obj, changes, path) => {
  if (path.length === 0) {
    return changes;
  }
  const defaultObj = obj ?? {};
  const item = path[0];
  const nextObj = defaultObj[item] ?? {};

  return {
    ...defaultObj,
    [item]: reducePath(nextObj, changes, path.slice(1)),
  };
};

export const useIdeaEditorHook = (ideaId) => {
  const [historyState, setHistoryState] = useState({
    undo: [],
    redo: [],
  });
  const [websocketState, setWebsocketState] = useState({
    otherUsers: {},
    changedFields: [],
    changes: false,
    websocketConnected: true,
    setChanged: false,
  });
  const [ideaState, setIdeaState] = useState({
    idea: {},
    isLoading: true,
    isLoaded: false,
    isSaving: false,
    savedAt: '',
    notFound: false,
    isSubmissible: true,
    wordCounts: [],
    incorrectFields: [],
    incorrectSections: [],
    errorMessage: '',
    history: [{}],
    historyIndex: -1,
  });
  const editTimeout = useRef(null);
  const websocketSubscription = useRef(null);
  const { user } = useStore().getState();
  const userId = user?._id;

  const saveIdea = useCallback((changes = {}) => new Promise(() => {
    if (util.localStorageIsSupported()) {
      const authToken = localStorage.getItem(`ideaAuthToken:${ideaId}`);
      changes.authToken = authToken;
    }
    setIdeaState((prevState) => ({ ...prevState, isSaving: true }));
    return api.ideas.update(ideaId, changes, ({
      submissibilityStatus,
    }) => {
      const {
        isSubmissible, incorrectFields, incorrectSections, wordCounts,
      } = submissibilityStatus;
      websocketSubscription && websocketSubscription.current.publish('ideaSaved');
      setWebsocketState((prevState) => ({ ...prevState, changedFields: [] }));
      setIdeaState((prevState) => ({
        ...prevState,
        isSubmissible,
        incorrectFields,
        incorrectSections,
        wordCounts,
        isSaving: false,
        savedAt: new Date(),
      }));
    }, (err) => {
      toast.error(err.message);
      setIdeaState((prevState) => ({ ...prevState, isSaving: false }));
    });
  }), [ideaId]);

  const getIdea = useCallback(() => {
    setIdeaState((prevState) => ({ ...prevState, isLoading: true, errorMessage: '' }));
    api.ideas.get(ideaId, (receivedIdea) => {
      setIdeaState((prevState) => ({
        ...prevState,
        idea: receivedIdea,
        notFound: false,
        isLoading: false,
        isLoaded: true,
        isSubmissible: receivedIdea.submissibilityStatus.isSubmissible,
        wordCounts: receivedIdea.submissibilityStatus.wordCounts,
        incorrectFields: receivedIdea.submissibilityStatus.incorrectFields,
        incorrectSections: receivedIdea.submissibilityStatus.incorrectSections,
      }));
    }, (err) => {
      setIdeaState((prevState) => ({
        ...prevState,
        idea: null,
        notFound: true,
        isLoading: false,
        errorMessage: err.message,
      }));
    });
  }, [ideaId]);

  useEffect(() => getIdea(), [getIdea]);

  const addHistoryEntry = useCallback((updatedIdea) => {
    const { templated, name, coverImage } = updatedIdea;
    setHistoryState((prevHistory) => {
      const slicedUndoHistory = prevHistory.undo.slice(Math.max(prevHistory.undo.length - 9, 0));
      return {
        ...prevHistory, undo: [...slicedUndoHistory, { templated, name, coverImage }],
      };
    });
  }, []);

  const undoChanges = useCallback(() => {
    const undoItem = historyState.undo[historyState.undo.length - 1];
    setIdeaState((prevIdeaState) => ({ ...prevIdeaState, idea: { ...prevIdeaState.idea, ...undoItem } }));
    setHistoryState((prevHistory) => ({
      redo: [...prevHistory.redo, { name: ideaState.idea.name, templated: ideaState.idea.templated, coverImage: ideaState.idea.coverImage }],
      undo: prevHistory.undo.slice(0, -1),
    }));
    saveIdea(undoItem);
  }, [historyState, saveIdea, ideaState.idea]);

  const redoChanges = useCallback(() => {
    const redoItem = historyState.redo[historyState.redo.length - 1];
    setIdeaState((prevIdeaState) => ({ ...prevIdeaState, idea: { ...prevIdeaState.idea, ...redoItem } }));
    setHistoryState((prevHistory) => ({
      redo: prevHistory.redo.slice(0, -1),
      undo: [...prevHistory.undo, { name: ideaState.idea.name, templated: ideaState.idea.templated, coverImage: ideaState.idea.coverImage }],
    }));
    saveIdea(redoItem);
  }, [historyState, saveIdea, ideaState.idea]);

  const handleKeyDown = useCallback((e) => {
    if (e.keyCode === 90 && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) {
        redoChanges();
      } else {
        undoChanges();
      }
    }
  }, [undoChanges, redoChanges]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const updateIdea = useCallback((update, path, suppressSave = false, callback = () => { }, setChanged = true) => {
    if (update === true && path.includes('isSubmitted')) {
      websocketSubscription.current.publish('ideaSubmitted');
    }

    if (setChanged) {
      setWebsocketState((prevWebsocketState) => ({ ...prevWebsocketState, changedFields: [...prevWebsocketState.changedFields, { update, path }] }));
    }

    let prevIdea = null;
    let updatedIdea = null;
    setIdeaState((prevState) => {
      prevIdea = prevState.idea;
      updatedIdea = reducePath(prevIdea, update, path);
      callback(updatedIdea);
      return {
        ...prevState,
        idea: updatedIdea,
      };
    });

    if (!suppressSave && editTimeout.current !== null) {
      clearTimeout(editTimeout.current);
      editTimeout.current = null;
    }
    editTimeout.current = setTimeout(() => {
      if (!suppressSave) {
        saveIdea(updatedIdea);
        websocketSubscription.current.publish('fieldEdited', { fieldId: path, fieldData: update });
        /*
          If current history index is < 0, and we're making an edit
          Then the user is continuing from an old edit (i.e. they undid a few times, then started editing again)
          Therefore the history state should be reset, with the new state
          The history index should also be reset to 0
          Else we just add to the history
        */
        addHistoryEntry(prevIdea);
        setHistoryState((prevHistory) => ({ ...prevHistory, redo: [] }));
      }
    }, 400);
  }, [saveIdea, addHistoryEntry, editTimeout]);

  const handleWebsocket = useCallback((data) => {
    const {
      channel, fieldId, fieldData, toUser, changedFields,
    } = data;
    // Discard any messages that were sent by the current user
    if ((toUser && toUser !== userId) || (data.user && data.user._id === userId)) {
      return;
    }

    switch (channel) {
      // Make sure that if a user opens an idea they get the proper response back if someone else was already in it to begin with
      case 'userSubscribed':
        websocketSubscription.current.publish('userWasSubscribed', { toUser: data.user._id, changedFields: websocketState.changedFields });
      // eslinst-disable-line no-fallthrough
      case 'userWasSubscribed':
        setWebsocketState((prevState) => ({ ...prevState, otherUsers: { ...prevState.otherUsers, [data.user._id]: data.user } }));
        if (changedFields && Array.isArray(changedFields)) {
          changedFields.forEach((change) => {
            updateIdea(change.data, change.path, true, () => { }, false);
          });
        }
        break;
      case 'userUnsubscribed':
        setWebsocketState((prevState) => {
          // eslint-disable-next-line no-unused-vars
          const { [data.user._id]: delUser, ...keepUsers } = prevState.otherUsers;
          return { ...prevState, otherUsers: keepUsers };
        });
        break;
      case 'ideaSubmitted':
        getIdea();
        toast(`${data.user.name} submitted the idea.`);
        break;
      case 'fieldEdited':
        setWebsocketState((prevState) => ({ ...prevState, otherUsers: { ...prevState.otherUsers, [data.user._id]: { isEditing: { fieldData, fieldId }, ...data.user } } }));
        updateIdea(fieldData, fieldId, true, () => { }, false);
        break;
      case '__websocketError':
        setWebsocketState((prevState) => ({ ...prevState, websocketConnected: false }));
        break;
      default:
        break;
    }
  }, [getIdea, userId, updateIdea, websocketState.changedFields]);

  // set up websocket connection for idea collaboration
  useEffect(() => {
    if (!websocketSubscription.current) {
      websocketSubscription.current = websocketApi.subscribe(`idea-${ideaId}`, (data) => handleWebsocket(data), {
        receiveUserUpdates: true,
        receiveErrorUpdates: true,
      });
    }
  }, [handleWebsocket, ideaId]);

  useEffect(() => () => {
    websocketSubscription.current.unsubscribe();
  }, []);

  useEffect(() => {
    const externalChanges = {};
    Object.values(websocketState.otherUsers).forEach((otherUser) => {
      const { isEditing } = otherUser;
      if (!isEditing) return;
      if (isEditing.fieldId.includes('templated')) {
        const templatedChanges = externalChanges.templated || {};
        const fieldKey = isEditing.fieldId.find((f) => f !== 'templated');
        const fieldChanges = templatedChanges[fieldKey] || [];
        if (fieldChanges.findIndex((u) => u._id === otherUser._id) === -1) {
          fieldChanges.push(otherUser);
          templatedChanges[fieldKey] = fieldChanges;
          externalChanges.templated = templatedChanges;
        }
      } else {
        const fieldChanges = externalChanges[isEditing.fieldId] || [];
        if (fieldChanges.findIndex((u) => u._id === otherUser._id) === -1) {
          fieldChanges.push(otherUser);
          externalChanges[isEditing.fieldId] = fieldChanges;
        }
      }
    });

    setWebsocketState((prevState) => ({ ...prevState, externalChanges }));
  }, [websocketState.otherUsers]);

  return {
    ideaState, updateIdea, undoChanges, redoChanges, canUndo: historyState.undo.length > 0, canRedo: historyState.redo.length > 0, otherUsers: websocketState.otherUsers, websocketConnected: websocketState.websocketConnected, externalChanges: websocketState.externalChanges,
  };
};

export default useIdeaEditorHook;
